import { ApiError } from './api';

/**
 * Reactive wrapper for the "submit a form, track submitting + error"
 * pattern that recurred across ProfileTab, DeleteConfirm, Login,
 * Register, etc. Cuts the boilerplate
 *
 *     let submitting = $state(false);
 *     let error = $state<ApiError | null>(null);
 *     async function save() {
 *       submitting = true;
 *       error = null;
 *       try { await api(...); }
 *       catch (err) { if (err instanceof ApiError) error = err; }
 *       finally { submitting = false; }
 *     }
 *
 * down to:
 *
 *     const save = new FormSubmit(async (newEmail: string) => {
 *       await api('/api/account/email', { method: 'PATCH', body: ... });
 *     });
 *     // handler: save.submit(emailValue)
 *     // template: <button disabled={save.submitting}>
 *     //          {#if save.error}<Banner>{save.error.message}</Banner>{/if}
 *
 * Non-ApiError exceptions are swallowed and surfaced as a generic
 * "Something went wrong" ApiError so the UI doesn't have to branch on
 * the failure source — every form-error render uses the same shape.
 */
export class FormSubmit<TArgs extends unknown[], TResult> {
  submitting = $state(false);
  error = $state<ApiError | null>(null);

  constructor(private readonly handler: (...args: TArgs) => Promise<TResult>) {}

  /** Run the handler. Returns the resolved value on success or `undefined`
   *  on failure (caller checks `submit.error` for details). */
  async submit(...args: TArgs): Promise<TResult | undefined> {
    this.submitting = true;
    this.error = null;
    try {
      return await this.handler(...args);
    } catch (err) {
      this.error = err instanceof ApiError
        ? err
        : new ApiError(0, 'Something went wrong. Try again.', null);
      return undefined;
    } finally {
      this.submitting = false;
    }
  }

  /** Manually clear the error — e.g. when the user starts editing again. */
  reset(): void {
    this.error = null;
  }
}
